import useUser from '@auth/useUser';
import useNavigation from 'src/components/theme-layouts/components/navigation/hooks/useNavigation';
import themesConfig from 'src/configs/themesConfig';
import { FuseSettingsConfigType } from '@fuse/core/FuseSettings/FuseSettings';
import { useMainTheme } from '@fuse/core/FuseSettings/hooks/fuseThemeHooks';
import useFuseSettings from '@fuse/core/FuseSettings/hooks/useFuseSettings';
import FuseSvgIcon from '@fuse/core/FuseSvgIcon';
import useNavigate from '@fuse/hooks/useNavigate';
import { Dialog, DialogContent, List, ListItem, ListItemButton, ListItemIcon, ListItemText, TextField, Typography } from '@mui/material';
import { useEffect, useMemo, useRef, useState } from 'react';
import { FuseFlatNavItemType } from '@/@fuse/core/FuseNavigation/types/FuseNavItemType';

type CommandPaletteDialogProps = {
    isOpen: boolean;
    setIsOpen: (open: boolean) => void;
};

type CommandItem = Partial<FuseFlatNavItemType> & {
    id: string;
    title: string;
    icon?: string;
    url?: string;
    action?: () => void;
    section?: string;
    category?: string;
};

/**
 * Creates a map of item IDs to their parent section titles
 */
function createParentMap(fullNavigation: ReturnType<typeof useNavigation>['navigation']): Map<string, string> {
    const parentMap = new Map<string, string>();

    fullNavigation.forEach((parent) => {
        if (parent.children) {
            parent.children.forEach((child) => {
                if (child.id) {
                    parentMap.set(child.id, parent.title || 'Pages');
                }

                // Handle nested children (e.g., MIS sub-sections)
                if (child.children) {
                    child.children.forEach((grandchild) => {
                        if (grandchild.id) {
                            parentMap.set(grandchild.id, parent.title || 'Pages');
                        }
                    });
                }
            });
        } else if (parent.id) {
            // Top-level items without children
            parentMap.set(parent.id, parent.title || 'Pages');
        }
    });

    return parentMap;
}

/**
 * Creates navigation command items from flat navigation
 */
function createNavItems(navigation: ReturnType<typeof useNavigation>['flattenNavigation'], parentMap: Map<string, string>): CommandItem[] {
    return (navigation || [])
        .filter((item) => item.title)
        .map((item) => ({
            id: item.id,
            title: `Go to ${item.title}`,
            icon: item.icon,
            url: item.url,
            category: 'Page',
            section: parentMap.get(item.id) || 'Pages'
        }));
}

/**
 * Handles theme toggle action
 */
function handleThemeToggle(
    mainTheme: ReturnType<typeof useMainTheme>,
    setSettings: ReturnType<typeof useFuseSettings>['setSettings'],
    isGuest: boolean,
    updateUserSettings: ReturnType<typeof useUser>['updateUserSettings']
) {
    const newTheme = mainTheme.palette.mode === 'dark' ? themesConfig.default : themesConfig.defaultDark;
    const newSettings = setSettings({
        theme: {
            main: newTheme,
            navbar: newTheme,
            toolbar: newTheme,
            footer: newTheme
        }
    } as Partial<FuseSettingsConfigType>);

    if (!isGuest) {
        updateUserSettings(newSettings);
    }
}

/**
 * Handles keyboard navigation
 */
function handleKeyDown(
    e: React.KeyboardEvent,
    filteredItems: CommandItem[],
    selectedIndexRef: React.RefObject<number>,
    isKeyboardNavigating: React.RefObject<boolean>,
    setSelectedIndex: (index: number) => void,
    handleItemClick: (item: CommandItem) => void,
    handleClose: () => void
) {
    if (e.key === 'ArrowDown') {
        handleArrowDown(e, filteredItems, selectedIndexRef, isKeyboardNavigating, setSelectedIndex);
        return;
    }

    if (e.key === 'ArrowUp') {
        handleArrowUp(e, filteredItems, selectedIndexRef, isKeyboardNavigating, setSelectedIndex);
        return;
    }

    if (e.key === 'Enter') {
        handleEnter(e, filteredItems, selectedIndexRef, handleItemClick);
        return;
    }

    if (e.key === 'Escape') {
        handleClose();
    }
}

function handleArrowDown(
    e: React.KeyboardEvent,
    filteredItems: CommandItem[],
    selectedIndexRef: React.RefObject<number>,
    isKeyboardNavigating: React.RefObject<boolean>,
    setSelectedIndex: (index: number) => void
) {
    e.preventDefault();
    e.stopPropagation();
    isKeyboardNavigating.current = true;
    const newIndex = (selectedIndexRef.current + 1) % filteredItems.length;
    selectedIndexRef.current = newIndex;
    setSelectedIndex(newIndex);
    setTimeout(() => { isKeyboardNavigating.current = false; }, 100);
}

function handleArrowUp(
    e: React.KeyboardEvent,
    filteredItems: CommandItem[],
    selectedIndexRef: React.RefObject<number>,
    isKeyboardNavigating: React.RefObject<boolean>,
    setSelectedIndex: (index: number) => void
) {
    e.preventDefault();
    e.stopPropagation();
    isKeyboardNavigating.current = true;
    const newIndex = (selectedIndexRef.current - 1 + filteredItems.length) % filteredItems.length;
    selectedIndexRef.current = newIndex;
    setSelectedIndex(newIndex);
    setTimeout(() => { isKeyboardNavigating.current = false; }, 100);
}

function handleEnter(
    e: React.KeyboardEvent,
    filteredItems: CommandItem[],
    selectedIndexRef: React.RefObject<number>,
    handleItemClick: (item: CommandItem) => void
) {
    e.preventDefault();

    if (filteredItems[selectedIndexRef.current]) {
        handleItemClick(filteredItems[selectedIndexRef.current]);
    }
}

/**
 * Command list item component
 */
type CommandListItemProps = {
    item: CommandItem;
    index: number;
    isSelected: boolean;
    listItemRefs: React.RefObject<(HTMLLIElement | null)[]>;
    isKeyboardNavigating: React.RefObject<boolean>;
    selectedIndexRef: React.RefObject<number>;
    setSelectedIndex: (index: number) => void;
    handleItemClick: (item: CommandItem) => void;
};

function CommandListItem({
    item,
    index,
    isSelected,
    listItemRefs,
    isKeyboardNavigating,
    selectedIndexRef,
    setSelectedIndex,
    handleItemClick
}: Readonly<CommandListItemProps>) {
    const handleMouseEnter = () => {
        if (!isKeyboardNavigating.current) {
            selectedIndexRef.current = index;
            setSelectedIndex(index);
        }
    };

    return (
        <ListItem
            key={item.id}
            disablePadding
            ref={(el) => { listItemRefs.current[index] = el; }}
        >
            <ListItemButton
                onClick={() => handleItemClick(item)}
                selected={isSelected}
                sx={{
                    backgroundColor: isSelected ? '#ff0000' : 'transparent',
                    '&:hover': {
                        backgroundColor: isSelected ? '#ff0000' : 'action.hover'
                    }
                }}
                onMouseEnter={handleMouseEnter}
            >
                <ListItemIcon className="min-w-40">
                    {item.icon ? (
                        <FuseSvgIcon size={20}>{item.icon}</FuseSvgIcon>
                    ) : (
                        <div className="w-20 h-20 flex items-center justify-center text-xs font-semibold">
                            {item.title?.[0]?.toUpperCase()}
                        </div>
                    )}
                </ListItemIcon>
                <ListItemText
                    primary={item.title}
                    secondary={`${item.category} · ${item.section}`}
                    slotProps={{
                        primary: {
                            sx: { fontSize: '1.125rem' }
                        },
                        secondary: {
                            sx: { fontSize: '1rem' }
                        }
                    }}
                />
            </ListItemButton>
        </ListItem>
    );
}

/**
 * Command palette dialog component
 */
function CommandPaletteDialog({ isOpen, setIsOpen }: Readonly<CommandPaletteDialogProps>) {
    const { flattenNavigation: navigation, navigation: fullNavigation } = useNavigation();
    const navigate = useNavigate();
    const { signOut, isGuest, updateUserSettings } = useUser();
    const { setSettings } = useFuseSettings();
    const mainTheme = useMainTheme();
    const [searchQuery, setSearchQuery] = useState('');
    const [selectedIndex, setSelectedIndex] = useState(0);
    const selectedIndexRef = useRef(0);
    const listItemRefs = useRef<(HTMLLIElement | null)[]>([]);
    const isKeyboardNavigating = useRef(false);

    // Example actions. You can extend this list or pass it as props in the future.
    const actions = useMemo<CommandItem[]>(() => [
        {
            id: 'action:home',
            title: 'Go Home',
            icon: 'heroicons-outline:home',
            url: '/',
            category: 'Action',
            section: 'Actions'
        },
        {
            id: 'action:refresh',
            title: 'Refresh page',
            icon: 'heroicons-outline:arrow-path',
            action: () => globalThis.window.location.reload(),
            category: 'Action',
            section: 'Actions'
        },
        {
            id: 'action:sign-out',
            title: 'Sign out',
            icon: 'heroicons-outline:arrow-right-on-rectangle',
            action: () => signOut(),
            category: 'Action',
            section: 'Account'
        },
        {
            id: 'action:toggle-theme',
            title: mainTheme.palette.mode === 'dark' ? 'Switch to Light Mode' : 'Switch to Dark Mode',
            icon: mainTheme.palette.mode === 'dark' ? 'heroicons-outline:sun' : 'heroicons-outline:moon',
            action: () => handleThemeToggle(mainTheme, setSettings, isGuest, updateUserSettings),
            category: 'Action',
            section: 'Settings'
        },
        {
            id: 'action:clear-configs',
            title: 'Debug: Clear Configs',
            icon: 'heroicons-outline:trash',
            action: () => {
                globalThis.window?.sessionStorage?.clear();
                globalThis.window?.location.reload();
            },
            category: 'Action',
            section: 'Debug'
        },
    ], [signOut, mainTheme, setSettings, updateUserSettings, isGuest]);

    const allItems = useMemo<CommandItem[]>(() => {
        const parentMap = createParentMap(fullNavigation);
        const navItems = createNavItems(navigation, parentMap);
        return [...actions, ...navItems];
    }, [navigation, actions, fullNavigation]);

    const filteredItems = useMemo(() => {
        if (!searchQuery.trim()) {
            return allItems.slice(0, 10); // Show first 10 items when no search
        }

        const query = searchQuery.toLowerCase();
        return allItems
            .filter((item) => item.title?.toLowerCase().includes(query))
            .slice(0, 10);
    }, [allItems, searchQuery]);

    // Reset selected index when search query changes
    useEffect(() => {
        setSelectedIndex(0);
        selectedIndexRef.current = 0;
    }, [searchQuery]);

    // Scroll selected item into view
    useEffect(() => {
        if (listItemRefs.current[selectedIndex]) {
            listItemRefs.current[selectedIndex]?.scrollIntoView({
                behavior: 'smooth',
                block: 'nearest'
            });
        }
    }, [selectedIndex]);

    useEffect(() => {
        function handler(e: KeyboardEvent) {
            // Cmd/Ctrl + K
            if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') {
                e.preventDefault();
                setIsOpen(true);
            }
        }

        document.addEventListener('keydown', handler);

        return () => document.removeEventListener('keydown', handler);
    }, [setIsOpen]);

    function handleClose() {
        setIsOpen(false);
        setSearchQuery('');
        setSelectedIndex(0);
        selectedIndexRef.current = 0;
    }

    function handleItemClick(item: CommandItem) {
        // If the item has a custom action function, call it
        if (item.action && typeof item.action === 'function') {
            try {
                item.action();
            } catch (error_) {
                // swallow errors
                console.warn(error_);
            }
            handleClose();
            return;
        }

        // otherwise, if there's a URL, navigate
        if (item.url) {
            navigate(item.url);
            handleClose();
        }
    }

    // Don't render anything if user is a guest (not authenticated)
    if (isGuest) {
        return null;
    }

    return (
        <Dialog
            open={isOpen}
            onClose={handleClose}
            maxWidth="sm"
            fullWidth
            slotProps={{
                paper: {
                    className: 'fixed top-[20%] m-0'
                }
            }}
        >
            <DialogContent className="p-0">
                <div className="p-8 border-b border-divider">
                    <TextField
                        autoFocus
                        fullWidth
                        placeholder="Search pages and actions..."
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        onKeyDown={(e) => handleKeyDown(e, filteredItems, selectedIndexRef, isKeyboardNavigating, setSelectedIndex, handleItemClick, handleClose)}
                        variant="standard"
                        slotProps={{
                            input: {
                                disableUnderline: true,
                                startAdornment: (
                                    <FuseSvgIcon size={20} className="mr-8" color="action">
                                        heroicons-outline:magnifying-glass
                                    </FuseSvgIcon>
                                ),
                            }
                        }}
                    />
                </div>

                <div className="py-4 max-h-[400px] overflow-auto">
                    {filteredItems.length === 0 ? (
                        <div className="p-16 text-center">
                            <Typography color="text.secondary">No results found</Typography>
                        </div>
                    ) : (
                        <List className="p-0">
                            {filteredItems.map((item, index) => (
                                <CommandListItem
                                    key={item.id}
                                    item={item}
                                    index={index}
                                    isSelected={index === selectedIndex}
                                    listItemRefs={listItemRefs}
                                    isKeyboardNavigating={isKeyboardNavigating}
                                    selectedIndexRef={selectedIndexRef}
                                    setSelectedIndex={setSelectedIndex}
                                    handleItemClick={handleItemClick}
                                />
                            ))}
                        </List>
                    )}
                </div>
            </DialogContent>
        </Dialog>
    );
}

export default CommandPaletteDialog;